import type { DataTableColumnDef } from 'components/data-table';
import { EMPTY } from './types';

type Column<Row, Id extends string> = DataTableColumnDef<Row> & { id: Id };

export const col = <
  Row extends Record<string, unknown>,
  Id extends string = string,
>(
  id: Id,
  header: string,
  size: number,
  meta: DataTableColumnDef<Row>['meta'] = { type: 'text' }
): Column<Row, Id> => ({ id, accessorKey: id, header, size, meta });

// Joined text: DataTable stacks an array value, which centres and clips it.
export const listCol = <
  Row extends Record<string, unknown>,
  Id extends string = string,
>(
  id: Id,
  header: string,
  size: number
): Column<Row, Id> => ({
  id,
  accessorFn: (row) => ((row[id] as string[]) ?? []).join(', ') || EMPTY,
  header,
  size,
  meta: { type: 'text', wrap: true },
});

const CITATION_COLUMNS = [
  col('documentType', 'Type', 280, { type: 'enum', wrap: true }),
  listCol('conveyors', 'Conveyors', 280),
  listCol('conveyees', 'Conveyees', 280),
  col('instrumentDate', 'Instrument Date', 160, { type: 'date' }),
  col('fileDate', 'File Date', 120, { type: 'date' }),
  col('recordedDate', 'Recorded Date', 120, { type: 'date' }),
  col('bookType', 'Book Type', 180),
  col('volume', 'Volume', 90),
  col('page', 'Page', 80),
  col('instrumentNumber', 'Instrument No.', 150),
  col('countyName', 'County', 140, { type: 'enum' }),
  col('stateName', 'State', 80),
];

export type CitationColumnId = (typeof CITATION_COLUMNS)[number]['id'];

// A recorded instrument's citation, as the runsheet shows it; the document pickers read the same columns.
// Keyed and read by id alone, so any row type takes them.
export const citationColumns = <Row extends Record<string, unknown>>() =>
  CITATION_COLUMNS as Column<Row, CitationColumnId>[];
